import * as React from "react";
import { Form, Accordion, Icon } from "semantic-ui-react";
import { DropdownOption } from "../../utils/pdApiUtils";
import DropdownField from "../../components/Editing/DropdownField";
import { parseConfig } from "../../utils/utils";

const MIN_MAX_ERROR_MESSAGE = "The minimum value must be less than or equal to the maximum value, and vice versa.";

export type EscalatorFaultConfigurationProps = {
  faultType?: string;
  displayReset?: string;
  indicatorOutput?: string;
  statisticsCounter?: string;
  faultLog?: string;
  krd?: string;
  faultTypeOptions: DropdownOption[];
  displayResetOptions: DropdownOption[];
  indicatorOutputOptions: DropdownOption[];
  statisticsCounterOptions: DropdownOption[];
  faultLogOptions: DropdownOption[];
  krdOptions: DropdownOption[];
  handleFaultConfigChanged: (fieldName: string, value: string) => void;
  permissions: boolean;
};

type SectionProps = {
  label: string;
  sectionKey: string;
  fieldName: string;
  currentValue: string | undefined;
  options: DropdownOption[];
  permissions: boolean;
  handleFaultConfigChanged: (fieldName: string, value: string) => void;
};

const ConfigSection: React.FC<SectionProps> = ({
  label,
  sectionKey,
  fieldName,
  currentValue,
  options,
  permissions,
  handleFaultConfigChanged,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [defVal, minVal, maxVal] = parseConfig(currentValue);
  const isMinMaxInvalid = minVal > maxVal;

  const handleChange = (index: 0 | 1 | 2, newVal: number): void => {
    const parts = parseConfig(currentValue);
    parts[index] = newVal;
    handleFaultConfigChanged(fieldName, `${parts[0]};${parts[1]};${parts[2]}`);
  };

  return (
    <Accordion fluid={true} styled={true}>
      <Accordion.Title active={isOpen} onClick={() => setIsOpen(prev => !prev)}>
        <Icon name="dropdown" />
        <span>
          <b>{label}</b>
        </span>
        {!isOpen && isMinMaxInvalid && (
          <p className="errorMessagePadding">{MIN_MAX_ERROR_MESSAGE}</p>
        )}
      </Accordion.Title>
      <Accordion.Content active={isOpen}>
        <DropdownField
          id={`${sectionKey}-faultConfigDefault`}
          label="Default"
          search={true}
          value={defVal}
          options={options}
          onChange={(value: number) => handleChange(0, value)}
          disabled={permissions}
        />
        <DropdownField
          id={`${sectionKey}-faultConfigMin`}
          label="Min"
          search={true}
          value={minVal}
          options={options}
          isError={isMinMaxInvalid}
          onChange={(value: number) => handleChange(1, value)}
          disabled={permissions}
        />
        <DropdownField
          id={`${sectionKey}-faultConfigMax`}
          label="Max"
          search={true}
          value={maxVal}
          options={options}
          isError={isMinMaxInvalid}
          onChange={(value: number) => handleChange(2, value)}
          disabled={permissions}
        />
        {isOpen && isMinMaxInvalid && (
          <p className="errorMsgDecPadding">{MIN_MAX_ERROR_MESSAGE}</p>
        )}
      </Accordion.Content>
    </Accordion>
  );
};

const EscalatorFaultConfiguration: React.FC<EscalatorFaultConfigurationProps> = ({
  faultType,
  displayReset,
  indicatorOutput,
  statisticsCounter,
  faultLog,
  krd,
  faultTypeOptions,
  displayResetOptions,
  indicatorOutputOptions,
  statisticsCounterOptions,
  faultLogOptions,
  krdOptions,
  handleFaultConfigChanged,
  permissions
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const sections: Array<{
    label: string;
    sectionKey: string;
    fieldName: string;
    currentValue: string | undefined;
    options: DropdownOption[];
  }> = [
    { label: "Fault Type (br)", sectionKey: "faultType", fieldName: "faultType", currentValue: faultType, options: faultTypeOptions },
    { label: "Display Reset (dI)", sectionKey: "displayReset", fieldName: "displayReset", currentValue: displayReset, options: displayResetOptions },
    { label: "Indicator Output (Ir)", sectionKey: "indicatorOutput", fieldName: "indicatorOutput", currentValue: indicatorOutput, options: indicatorOutputOptions },
    { label: "Statistics Counter (Co)", sectionKey: "statisticsCounter", fieldName: "statisticsCounter", currentValue: statisticsCounter, options: statisticsCounterOptions },
    { label: "Fault Log to Customer (FL)", sectionKey: "faultLog", fieldName: "faultLog", currentValue: faultLog, options: faultLogOptions },
    { label: "KRD (rD)", sectionKey: "krd", fieldName: "krd", currentValue: krd, options: krdOptions },
  ];

  const hasAnyInvalid = sections.some(section => {
    const [, minVal, maxVal] = parseConfig(section.currentValue);
    return minVal > maxVal;
  });

  return (
    <Form.Group id="escalatorFaultConfiguration" grouped={true}>
      <Form.Field>
        <Accordion fluid={true} styled={true}>
          <Accordion.Title active={isOpen} onClick={() => setIsOpen(prev => !prev)}>
            <Icon name="dropdown" />
            <span>
              <b>Fault configuration</b>
            </span>
            {!isOpen && hasAnyInvalid && (
              <p className="errorMessagePadding">{MIN_MAX_ERROR_MESSAGE}</p>
            )}
          </Accordion.Title>
          <Accordion.Content active={isOpen}>
            {sections.map(section => (
              <ConfigSection
                key={section.sectionKey}
                {...section}
                permissions={permissions}
                handleFaultConfigChanged={handleFaultConfigChanged}
              />
            ))}
          </Accordion.Content>
        </Accordion>
      </Form.Field>
    </Form.Group>
  );
};

export default EscalatorFaultConfiguration;
